<template>
  <div class="work-experience">
    <ResumeForm
      :formLine="formData"
      :data="workExperience"
      ref="form"
    >
      <template #dateRange="{ data, line }">
        <van-row class="row" :gutter="4">
          <van-col :span="11">
            <VanCustomField
              class="start-date"
              :field="line.startDate"
              :data="data"
            >
            </VanCustomField>
          </van-col>
          <van-col :span="2">
            <div class="line">{{ $t(`${i18nPrefix}.label.to`) }}</div>
          </van-col>
          <van-col :span="11">
            <VanCustomField
              class="end-date"
              :field="line.endDate"
              :data="data"
            >
            </VanCustomField>
          </van-col>
        </van-row>
      </template>
       <template #salary="{ data, line }">
        <van-row class="row" :gutter="20">
          <van-col :span="12">
            <VanCustomField
              :field="line.currency"
              :data="data"
            >
            </VanCustomField>
          </van-col>
          <van-col :span="12">
            <VanCustomField
              :field="line.salary"
              :data="data"
            >
            </VanCustomField>
          </van-col>
        </van-row>
      </template>
    </ResumeForm>
  </div>
</template>

<script>
import _ from 'lodash'
import ResumeForm from '@/components/mobile/resumeForm.vue'
import VanCustomField from '@/components/mobile/vanCustomField.vue'
import moment from 'moment'
import { STEP_TYPE } from '@/constant/candidate'

export default {
  components: {
    ResumeForm,
    VanCustomField
  },
  props: {
    workExperience: {
      type: Object,
      required: true
    },
    isMiniResume: {
      type: Boolean,
      default: false
    },
    resumeEditorSetting: {
      type: Object,
      default: null
    },
    countryOptions: {
      type: Array,
      default: null
    },
    provinceOptions: {
      type: Array,
      default: null
    },
    cityOptions: {
      type: Array,
      default: null
    },
    jobFamilyOptions: {
      type: Array,
      default: null
    },
    industryOptions: {
      type: Array,
      default: null
    },
    currencyOptions: {
      type: Array,
      default: null
    },
    salaryOptions: {
      type: Array,
      default: null
    },
    index: {
      default: null
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      countryChangeLoading: true,
      provinceChangeLoading: true
    }
  },
  computed: {
    setting () {
      return this.resumeEditorSetting?.[STEP_TYPE.WORK_EXPERIENCE]
    },
    hiddenField () {
      if (this.isMiniResume) {
        const hiddenField = [
          'jobFamily',
          'industry',
          'country',
          'province',
          'city',
          'salary'
        ]
        if (this.setting?.hiddenField) {
          hiddenField.push(...this.setting?.hiddenField)
        }
        return hiddenField
      }
      return this.setting?.hiddenField || []
    },
    displayField () {
      return this.setting?.displayField || []
    },
    requiredField () {
      return this.setting?.requiredField || []
    },
    disabledField () {
      return this.setting?.disabledField || []
    },
    formData () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const requiredField = ['companyName', 'jobTitle', 'jobFamily', 'industry', 'country', 'province', 'city', 'startDate']
      if (this.requiredField) {
        requiredField.push(...this.requiredField)
      }
      const hiddenField = []
      if (this.hiddenField) {
        hiddenField.push(...this.hiddenField)
      }
      const displayField = []
      if (this.displayField) {
        displayField.push(...this.displayField)
      }
      const disabledField = []
      if (this.disabledField) {
        disabledField.push(...this.disabledField)
      }
      const vm = this
      return [
        {
          elType: 'input',
          name: 'companyName',
          hidden: hiddenField.includes('companyName'),
          label: this.$t(`${this.i18nPrefix}.label.companyName`),
          rules: [{ required: requiredField.includes('companyName'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'input',
          name: 'jobTitle',
          hidden: hiddenField.includes('jobTitle'),
          label: this.$t(`${this.i18nPrefix}.label.jobTitle`),
          rules: [{ required: requiredField.includes('jobTitle'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'jobFamily',
          hidden: hiddenField.includes('jobFamily'),
          options: { data: this.jobFamilyOptions },
          label: this.$t(`${this.i18nPrefix}.label.jobFamily`),
          rules: [{ required: requiredField.includes('jobFamily'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'industry',
          hidden: hiddenField.includes('industry'),
          options: { data: this.industryOptions },
          label: this.$t(`${this.i18nPrefix}.label.industry`),
          rules: [{ required: requiredField.includes('industry'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'country',
          hidden: hiddenField.includes('country'),
          options: {
            data: this.countryOptions
          },
          handler: (data, countryId) => {
            this.handleCountryChange(countryId)
          },
          label: this.$t(`${this.i18nPrefix}.label.workCountry`),
          rules: [{ required: requiredField.includes('country'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'province',
          hidden: hiddenField.includes('province'),
          options: {
            data: this.provinceOptions
          },
          handler: (data, provinceId) => {
            this.handleProvinceChange(provinceId)
          },
          label: this.$t(`${this.i18nPrefix}.label.workProvince`),
          rules: [{ required: requiredField.includes('province'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'city',
          hidden: hiddenField.includes('city'),
          options: {
            data: this.cityOptions
          },
          label: this.$t(`${this.i18nPrefix}.label.workCity`),
          rules: [{ required: requiredField.includes('city'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          slotName: 'dateRange',
          startDate: {
            elType: 'date',
            name: 'startDate',
            hidden: hiddenField.includes('startDate'),
            formatter: (value) => {
              return value ? moment(value).format('YYYY-MM') : ''
            },
            placeholder: this.$t(`${this.i18nPrefix}.placeholder.startDate`),
            label: this.$t(`${this.i18nPrefix}.label.date`),
            dateTitle: this.$t(`${this.i18nPrefix}.placeholder.startDate`),
            rules: [{ required: requiredField.includes('startDate'), message: requiredMessage, trigger: 'blur' }]
          },
          endDate: {
            elType: 'date',
            name: 'endDate',
            hidden: hiddenField.includes('endDate'),
            formatter: (value) => {
              return value ? moment(value).format('YYYY-MM') : ''
            },
            get placeholder () {
              if (vm.index > 0) {
                return vm.$t(`${vm.i18nPrefix}.placeholder.endDate`)
              } else {
                return vm.$t(`${vm.i18nPrefix}.placeholder.currentDate`)
              }
            },
            label: '',
            dateTitle: this.$t(`${this.i18nPrefix}.placeholder.endDate`),
            rules: [{ required: requiredField.includes('endDate'), message: requiredMessage, trigger: 'blur' }]
          }
        },
        {
          hidden: hiddenField.includes('salary'),
          slotName: 'salary',
          currency: {
            elType: 'select',
            name: 'currency',
            options: { data: this.currencyOptions },
            label: this.$t(`${this.i18nPrefix}.label.salary`),
            rules: [{ required: requiredField.includes('currency'), message: requiredMessage, trigger: 'blur' }]
          },
          salary: {
            elType: 'select',
            name: 'salary',
            options: { data: this.salaryOptions },
            label: '',
            rules: [{ required: requiredField.includes('salary'), message: requiredMessage, trigger: 'blur' }]
          }
        },
        {
          elType: 'text',
          name: 'summary',
          hidden: hiddenField.includes('summary'),
          label: this.$t(`${this.i18nPrefix}.label.description`),
          rules: [{ required: requiredField.includes('summary'), message: requiredMessage, trigger: 'blur' }]
        }
      ]
    },
    initLoading () {
      return this.countryChangeLoading || this.provinceChangeLoading
    }
  },
  created () {
    if (_.isEmpty(this.provinceOptions) && this.workExperience.country) {
      this.handleCountryChange(this.workExperience.country, () => {
        this.countryChangeLoading = false
      })
    } else {
      this.countryChangeLoading = false
    }
    if (_.isEmpty(this.cityOptions) && this.workExperience.province) {
      this.handleProvinceChange(this.workExperience.province, () => {
        this.provinceChangeLoading = false
      })
    } else {
      this.provinceChangeLoading = false
    }
  },
  methods: {
    handleCountryChange (countryId, callback) {
      if (!countryId) {
        return
      }
      this.$emit('country-change', { countryId, callback }
      )
    },
    handleProvinceChange (provinceId, callback) {
      if (!provinceId) {
        return
      }
      this.$emit('province-change', { provinceId, callback })
    },
    async submit () {
      const form = await this.getForm()
      if (form) {
        const result = await form.submit()
        return result
      }
      return false
    },
    async getForm () {
      let form = this.$refs.form
      if (!form) {
        await this.$nextTick()
        form = this.$refs.form
      }
      return form
    }
  },
  watch: {
    initLoading: {
      immediate: true,
      handler () {
        this.$emit('load', this.initLoading)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.work-experience {

  .row {
    margin-bottom: 16px;

    .line {
      margin: 0 auto;
      position: relative;
      top: 24px;
      width: 10px;
      color: #909399;
      font-size: 14px;
    }
  }
}

</style>
